import React, { useState, useEffect } from 'react';
import { ProbationInstructionItem, ProbationInstructionItemFormData, Employee } from '../../types/models';
import { ProbationInstructionItemService } from '../../services/probationInstructionItemService';
import { EmployeeService } from '../../services/employeeService';
import { EmployeeAutocomplete } from '../EmployeeAutocomplete';
import { formatDateForInput } from '../../utils/dateUtils';

interface ProbationInstructionItemFormProps {
  instruction?: ProbationInstructionItem;
  employeeId?: number;
  onSuccess: (createdItem?: ProbationInstructionItem) => void;
  onCancel: () => void;
}

export const ProbationInstructionItemForm: React.FC<ProbationInstructionItemFormProps> = ({
  instruction,
  employeeId,
  onSuccess,
  onCancel
}) => {
  const [formData, setFormData] = useState<ProbationInstructionItemFormData>({
    employeeId: instruction?.employeeId || employeeId || 0,
    responsibleEmployeeId: instruction?.responsibleEmployeeId || 0,
    startDate: instruction?.startDate ? new Date(instruction.startDate).toISOString().split('T')[0] : '',
    finishDate: instruction?.finishDate ? new Date(instruction.finishDate).toISOString().split('T')[0] : '',
    docDate: instruction?.docDate ? new Date(instruction.docDate).toISOString().split('T')[0] : '',
    shiftCount: instruction?.shiftCount || 0,
    withDuplication: instruction?.withDuplication || false,
    numDoc: instruction?.numDoc || '',
  });

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Загружаем всех сотрудников
      const employeesResponse = await EmployeeService.getAll();

      if (employeesResponse.success) {
        setEmployees(employeesResponse.data);
      }
    } catch (err) {
      console.error('Ошибка загрузки данных:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      let response;
      if (instruction) {
        // Обновление
        response = await ProbationInstructionItemService.update(instruction.id, { ...formData, id: instruction.id });
      } else {
        // Создание
        response = await ProbationInstructionItemService.create(formData);
      }

      if (response.success) {
        // Для создания передаем созданный элемент, для обновления - ничего
        onSuccess(instruction ? undefined : (response.data as ProbationInstructionItem));
      } else {
        setError(response.message || 'Ошибка сохранения');
      }
    } catch (err) {
      setError('Произошла ошибка при сохранении');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : name.includes('Id') ? Number(value) : value
    }));
  };

  return (
    <div className="modal-overlay">
      <div
        className="modal-content"
        style={{ maxWidth: '800px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card" style={{ margin: 0, boxShadow: 'none' }}>
          <h3>{instruction ? 'Редактирование стажировки' : 'Создание стажировки'}</h3>

          {error && (
            <div className="alert alert-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Сотрудник *</label>
            <EmployeeAutocomplete
              value={formData.employeeId}
              onChange={(employeeId) => setFormData(prev => ({ ...prev, employeeId: employeeId || 0 }))}
              placeholder="Начните вводить ФИО сотрудника..."
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Ответственный сотрудник *</label>
            <EmployeeAutocomplete
              value={formData.responsibleEmployeeId}
              onChange={(employeeId) => setFormData(prev => ({ ...prev, responsibleEmployeeId: employeeId || 0 }))}
              placeholder="Начните вводить ФИО ответственного..."
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Дата начала *</label>
            <input
              type="date"
              name="startDate"
              className="form-input"
              value={formatDateForInput(formData.startDate)}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Дата окончания *</label>
            <input
              type="date"
              name="finishDate"
              className="form-input"
              value={formatDateForInput(formData.finishDate)}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Дата распоряжения *</label>
            <input
              type="date"
              name="docDate"
              className="form-input"
              value={formatDateForInput(formData.docDate)}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Количество смен *</label>
            <input
              type="number"
              name="shiftCount"
              className="form-input"
              value={formData.shiftCount}
              onChange={handleChange}
              min="0"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">С дублированием</label>
            <input
              type="checkbox"
              name="withDuplication"
              checked={formData.withDuplication}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Номер распоряжения</label>
            <input
              type="text"
              name="numDoc"
              className="form-input"
              value={formData.numDoc}
              onChange={handleChange}
            />
          </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Сохранение...' : (instruction ? 'Сохранить' : 'Создать')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Отмена
          </button>
          </div>
          </form>
        </div>
      </div>
    </div>
  );
};